import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { AppShell, Button, Icon, NAV_ITEMS, Sidebar } from '@suvoda-uat/ui-suvoda';
import { AssemblyReviewView } from '../components/AssemblyReviewView.js';
import { LifecycleTopBar } from '../components/LifecycleTopBar.js';
import {
  DownloadView,
  useDownloadTestScript,
  type DownloadNotAvailable,
} from '../components/DownloadView.js';
import {
  InterimDownloadView,
  useInterimExport,
  type InterimDownloadAvailable,
  type InterimDownloadNotAvailable,
} from '../components/InterimDownloadView.js';
import { FinaliseControl } from '../components/FinaliseControl.js';
import { interimReadyCount } from '../lib/assembly.js';
import { signOut } from '../lib/auth.js';
import { type NavItem, visibleNavItems } from '../lib/nav.js';
import { createLifecycleBeforeLoad } from '../lib/lifecycle.js';
import { useAssemblyPosition } from '../lib/use-assembly-position.js';
import { useTestScript, useTrial } from '../lib/queries.js';
import type { Trial } from '../lib/api-client.js';

export const uatBeforeLoad = createLifecycleBeforeLoad('/uat');
export const UAT_SHELL_CONTENT_CLASS_NAME =
  'p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

export const isNestedCaseRoutePath = (pathname: string): boolean =>
  pathname.startsWith('/uat/cases/');

export const Route = createFileRoute('/uat')({
  validateSearch: (search: Record<string, unknown>): { trialId: string } => ({
    trialId: typeof search['trialId'] === 'string' ? search['trialId'] : '',
  }),
  beforeLoad: uatBeforeLoad,
  component: UATRoute,
});

export type AssemblyViewProps = {
  trialId: string;
  trial?: Trial | undefined;
  /** Section ids that should start expanded, used by route/stateful stories. */
  initialOpenSectionIds?: readonly string[];
  /** Subsection ids that should start expanded, used by route/stateful stories. */
  initialOpenSubsectionIds?: readonly string[];
  /** Test-ID filter to seed on mount (F-024 restore, FR-012). */
  initialQuery?: string;
  /** Controlled filter — when provided, the navbar search drives the assembly filter. */
  query?: string;
  /** Jump to an unresolved Test Case from the DownloadGate. */
  onSelectTestCase?: (testCaseId: string) => void;
  /** The PrepareDownload gate result, owned by the topbar Download trigger (FR-034). */
  downloadNotAvailable?: DownloadNotAvailable | null;
  /** F-149: the interim-download result, owned by the topbar "Download current progress" trigger. */
  interimAvailable?: InterimDownloadAvailable | null;
  interimNotAvailable?: InterimDownloadNotAvailable | null;
  /** F-024 capture seams — forwarded to AssemblyReviewView (open Sections/Subsections/filter). */
  onOpenSectionIdsChange?: (ids: readonly string[]) => void;
  onOpenSubsectionIdsChange?: (ids: readonly string[]) => void;
  onQueryChange?: (query: string) => void;
};

/**
 * Assembly view body (FR-011): the review of the generated Test Script. The
 * engine's per–Test-Case recommendations are surfaced by `AssemblyReviewView`
 * (Sections → Subsections → scored Test Cases, with triage); the US3 Download
 * flow gates on PrepareDownload (ready → the browser downloads the DOCX;
 * outstanding → the DownloadGate lists the unresolved Test Cases, FR-034). Kept
 * props-only so it is testable without a router; the route below feeds it
 * `trialId` (from search) + navigation.
 */
export function AssemblyView({
  trialId,
  trial,
  initialOpenSectionIds = [],
  initialOpenSubsectionIds = [],
  initialQuery = '',
  query,
  onSelectTestCase,
  downloadNotAvailable = null,
  interimAvailable = null,
  interimNotAvailable = null,
  onOpenSectionIdsChange,
  onOpenSubsectionIdsChange,
  onQueryChange,
}: AssemblyViewProps) {
  return (
    <div className="pb-2">
      <AssemblyReviewView
        trialId={trialId}
        initialOpenSectionIds={initialOpenSectionIds}
        initialOpenSubsectionIds={initialOpenSubsectionIds}
        initialQuery={initialQuery}
        {...(query !== undefined && { query })}
        {...(onSelectTestCase && { onSelectTestCase })}
        {...(onOpenSectionIdsChange && { onOpenSectionIdsChange })}
        {...(onOpenSubsectionIdsChange && { onOpenSubsectionIdsChange })}
        {...(onQueryChange && { onQueryChange })}
      />
      <div className="mx-6 mt-3 flex flex-wrap items-start justify-end gap-3">
        {trial ? <FinaliseControl trial={trial} /> : null}
        <DownloadView
          trialId={trialId}
          notAvailable={downloadNotAvailable}
          {...(onSelectTestCase && { onSelectTestCase })}
        />
        <InterimDownloadView available={interimAvailable} notAvailable={interimNotAvailable} />
      </div>
    </div>
  );
}

export type UATRouteFrameProps = {
  readonly trialId: string;
  readonly trial?: Trial | undefined;
  readonly isNestedCaseRoute: boolean;
  readonly onSelectTestCase: (testCaseId: string) => void;
  /** Sidebar "Dashboard" destination — kept props-only so the frame stays router-free. */
  readonly onNavigateToDashboard?: () => void;
  /** Sidebar items — the route passes `visibleNavItems()` (Team is admin-only). */
  readonly navItems?: readonly NavItem[];
  /** Sidebar "Team" destination (feature 015), props-only like Dashboard. */
  readonly onNavigateToTeam?: () => void;
  readonly children?: ReactNode;
};

export function UATRouteFrame({
  trialId,
  trial,
  isNestedCaseRoute,
  onSelectTestCase,
  onNavigateToDashboard,
  navItems = NAV_ITEMS,
  onNavigateToTeam,
  children,
}: UATRouteFrameProps) {
  // F-024 (UAT-09): read this trial's in-session place + the write-through
  // capture handlers. Called before the nested early-return so the hook persists
  // across the case-route unmount — this frame stays mounted while only the
  // AssemblyView subtree remounts, so each return re-seeds the captured place.
  // Scroll restore gates on the Test Script data being present (real list
  // height, not the "Loading…" placeholder); React Query dedupes this read with
  // AssemblyReviewView's.
  const script = useTestScript(trialId);
  const position = useAssemblyPosition(trialId, script.data !== undefined);
  // FR-034: the Download trigger lives in the topbar; its gate result is shared
  // with the Assembly body's DownloadView so a click there surfaces the gate.
  const download = useDownloadTestScript(trialId);
  // F-149 (US1): the interim ("Download current progress") trigger is also owned
  // here so its result feeds the Assembly body's InterimDownloadView banner/hint.
  const interim = useInterimExport(trialId);
  // FR-011 (US3): the interim affordance is UNAVAILABLE until ≥ 1 case is
  // Included + Complete (else there is nothing to export — no empty document).
  const interimReady = script.data !== undefined && interimReadyCount(script.data) > 0;
  // The Test-ID/name filter is owned HERE so the navbar (sidebar) search drives
  // the same filter as the assembly content search — both stay in sync. Seeded
  // from the restored place and written through to the F-024 capture.
  const [query, setQuery] = useState(position.initialQuery);
  const onQueryChange = (value: string) => {
    setQuery(value);
    position.onQueryChange(value);
  };

  if (isNestedCaseRoute) return <>{children}</>;

  // Topbar action (Figma): the download glyph + "Download", 10px side padding,
  // pinned to the right of the top bar. F-149 (FR-008): a SEPARATE, distinctly-
  // labelled "Download current progress" control sits beside it while the trial
  // is in review, so an interim draft is never mistaken for the finalised export.
  const downloadAction = (
    <div className="flex items-center gap-2">
      {trial?.status === 'in_review' ? (
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Icon name="download" size={14} />}
          disabled={interim.pending || !interimReady}
          onClick={interim.onDownload}
          className="gap-1 px-[10px]"
          {...(interimReady
            ? {}
            : { title: 'Complete at least one included case to download your progress.' })}
        >
          Download current progress
        </Button>
      ) : null}
      <Button
        variant="primary"
        size="sm"
        leftIcon={<Icon name="download" size={14} />}
        disabled={download.pending}
        onClick={download.onDownload}
        className="gap-1 px-[10px]"
      >
        Download
      </Button>
    </div>
  );

  return (
    <AppShell
      contentRef={position.contentRef}
      sidebar={
        <Sidebar
          items={[...navItems]}
          activeId="uat"
          searchPlaceholder="What are you looking for?"
          searchValue={query}
          onSearchChange={onQueryChange}
          onNavigate={(id) => {
            if (id === 'dashboard') onNavigateToDashboard?.();
            if (id === 'team') onNavigateToTeam?.();
          }}
          onSignOut={() => signOut()}
        />
      }
      topbar={
        <LifecycleTopBar
          currentStep="assembly"
          showStage={false}
          trial={trial}
          actions={downloadAction}
        />
      }
      contentClassName={UAT_SHELL_CONTENT_CLASS_NAME}
    >
      <AssemblyView
        trialId={trialId}
        trial={trial}
        initialOpenSectionIds={position.initialOpenSectionIds}
        initialOpenSubsectionIds={position.initialOpenSubsectionIds}
        initialQuery={position.initialQuery}
        query={query}
        onOpenSectionIdsChange={position.onOpenSectionIdsChange}
        onOpenSubsectionIdsChange={position.onOpenSubsectionIdsChange}
        onQueryChange={onQueryChange}
        onSelectTestCase={onSelectTestCase}
        downloadNotAvailable={download.notAvailable}
        interimAvailable={interim.available}
        interimNotAvailable={interim.notAvailable}
      />
    </AppShell>
  );
}

/**
 * Assembly destination (FR-011). Generation success navigates here (carrying
 * the trialId) to review the generated Test Script — the engine's
 * inclusion/exclusion recommendations — and trigger the US3 download.
 */
export function UATRoute() {
  const { trialId } = Route.useSearch();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const trial = useTrial(trialId);
  return (
    <UATRouteFrame
      trialId={trialId}
      trial={trial.data}
      isNestedCaseRoute={isNestedCaseRoutePath(pathname)}
      navItems={visibleNavItems()}
      onNavigateToDashboard={() => void navigate({ to: '/' })}
      onNavigateToTeam={() => void navigate({ to: '/team' })}
      onSelectTestCase={(testCaseId) =>
        navigate({
          to: '/uat/cases/$testId',
          params: { testId: testCaseId },
          search: { trialId },
        })
      }
    >
      <Outlet />
    </UATRouteFrame>
  );
}
